import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { rawDocumentId } from '../functions/document-id';
import { ArchiveDocumentRef, ArchiveDocumentService } from './archive-document.service';

export interface ArchiveTarget extends ArchiveDocumentRef {
  /** Belegnummer or Bezugsnummer — the panel header's subtitle. */
  label: string | null;
}

type DocumentKey = Pick<ArchiveDocumentRef, 'documentId' | 'variant'>;

/** kopferw and kopfek ids come from two tables and can collide, so the Belegvariante
 *  is part of the identity; the id is compared raw because the query service answers
 *  bare ids where the contract promises prefixed ones. */
function sameDocument(a: DocumentKey, b: DocumentKey): boolean {
  return a.variant === b.variant && rawDocumentId(a.documentId) === rawDocumentId(b.documentId);
}

/** Which row the archive side panel shows, and whether it is open. Page-scoped: the
 *  cell renderers write to it, the page reads it. */
@Injectable()
export class ArchivePanelState {
  private readonly archive = inject(ArchiveDocumentService);
  private readonly injector = inject(Injector);

  readonly visible = signal(false);
  readonly target = signal<ArchiveTarget | null>(null);
  readonly label = computed(() => this.target()?.label ?? null);

  /** What `soft-ds-file-view` mounts. A fresh promise per fetch, so the viewer's own
   *  resource reloads; null resolves to its empty state. */
  readonly file = signal<Promise<Blob | null> | null>(null);
  readonly fileName = signal('');

  open(target: ArchiveTarget): void {
    const current = this.target();
    this.visible.set(true);
    if (current && sameDocument(current, target)) return;
    this.target.set(target);
    this.load(target);
  }

  close(): void {
    this.visible.set(false);
  }

  reload(): void {
    const target = this.target();
    if (target) this.load(target);
  }

  isOpenFor(ref: DocumentKey): boolean {
    const target = this.target();
    return this.visible() && !!target && sameDocument(target, ref);
  }

  private load(target: ArchiveTarget): void {
    this.fileName.set('');
    this.file.set(
      this.archive.loadArchiveDocument(target).then(
        (file) => {
          this.fileName.set(file?.name ?? '');
          return file?.blob ?? null;
        },
        (error: unknown) => {
          // The viewer shows the failure inline; this keeps the log and the 401 handling.
          handleRequestError(error, this.injector);
          throw error;
        }
      )
    );
  }
}
